// Shared GS1 utilities for scanner providers
// This module contains pure functions and utilities that can be used across different scanner implementations

export interface GS1Identifier {
  ai: string
  value: string
}

export interface BarcodeCollection {
  barcodeData: string
  symbologyName: string
  timestamp: number
}

/**
 * Checks if a barcode text is a GS1 UDI (Unique Device Identifier)
 * Supports multiple GS1 formats: bracketed, unbracketed, and GTIN-only
 */
export function isGS1UDI(barcodeText: string): boolean {
  // GS1 UDIs can be in multiple formats:
  // 1. With brackets: (01)12345678901234(17)230930(10)ABC123
  // 2. Without brackets: 01123456789012341723093010ABC123
  // 3. Just GTIN: 12345678901234

  // Check for bracketed format
  const bracketedPattern = /^\(01\)\d{14}/
  if (bracketedPattern.test(barcodeText)) {
    return true
  }

  // Check for unbracketed format starting with 01
  const unbracketedPattern = /^01\d{14}/
  if (unbracketedPattern.test(barcodeText)) {
    return true
  }

  // Check for just GTIN (14 digits)
  const gtinPattern = /^\d{14}$/
  if (gtinPattern.test(barcodeText)) {
    return true
  }

  // Check for other GS1 Application Identifiers
  const hasOtherIdentifiers = /(?:17|10|21)\d+/.test(barcodeText)
  if (hasOtherIdentifiers) {
    return true
  }

  return false
}

/**
 * Parses a GS1 barcode text into application identifiers
 * Handles both bracketed and unbracketed formats with variable and fixed-length AIs
 */
export function parseGS1Barcode(barcodeText: string): GS1Identifier[] {
  const identifiers: GS1Identifier[] = []

  // Handle bracketed format: (01)12345678901234(17)230930(10)ABC123
  if (barcodeText.includes('(')) {
    const matches = barcodeText.match(/\((\d{2})\)([^()]+)/g)
    if (matches) {
      matches.forEach((match) => {
        const ai = match.match(/\((\d{2})\)/)?.[1]
        const value = match.match(/\)([^()]+)/)?.[1]
        if (ai && value) {
          identifiers.push({ ai, value })
        }
      })
    }
    return identifiers
  }

  // GS1 Application Identifier lengths
  const aiLengths: { [key: string]: number } = {
    '00': 18, // SSCC
    '01': 14, // GTIN
    '02': 14, // GTIN of contained trade items
    '10': -1, // Lot number (variable length)
    '11': 6, // Production date
    '13': 6, // Packaging date
    '15': 6, // Best before date
    '17': 6, // Expiration date
    '20': 2, // Product variant
    '21': -1, // Serial number (variable length)
    '22': -1, // Consumer product variant (variable length)
    '30': -1, // Variable count of items (variable length)
    '37': -1, // Count of trade items (variable length)
    '90': -1, // Internal company information (variable length)
    '91': -1, // Internal company information (variable length)
    '92': -1, // Internal company information (variable length)
    '93': -1, // Internal company information (variable length)
    '94': -1, // Internal company information (variable length)
    '95': -1, // Internal company information (variable length)
    '96': -1, // Internal company information (variable length)
    '97': -1, // Internal company information (variable length)
    '98': -1, // Internal company information (variable length)
    '99': -1, // Internal company information (variable length)
  }

  // Handle unbracketed format
  let remaining = barcodeText

  // Special handling for common GS1 patterns
  // Look for GTIN (01) followed by lot number (10) and expiration date (17)
  // eslint-disable-next-line no-control-regex
  const gtinLotExpPattern = /^01(\d{14})10([^\x1D]+)\x1D17(\d{6})$/
  const match = barcodeText.match(gtinLotExpPattern)
  if (match) {
    identifiers.push({ ai: '01', value: match[1] })
    identifiers.push({ ai: '10', value: match[2] })
    identifiers.push({ ai: '17', value: match[3] })
    return identifiers
  }

  while (remaining.length > 0) {
    let foundMatch = false

    // Try to find fixed-length AIs first
    for (const [ai, length] of Object.entries(aiLengths)) {
      if (length === -1) continue // Skip variable length AIs for now

      if (remaining.startsWith(ai)) {
        // Check if we have enough characters for the full value
        if (remaining.length >= ai.length + length) {
          const value = remaining.substring(ai.length, ai.length + length)

          // Check if there's a next AI
          const afterValue = remaining.substring(ai.length + length)

          // For fixed-length AIs, we always take the exact length
          identifiers.push({ ai, value })
          remaining = afterValue
          foundMatch = true
          break
        }
      }
    }

    // If no fixed-length AI was found, try variable-length AIs
    if (!foundMatch) {
      for (const [ai, length] of Object.entries(aiLengths)) {
        if (length !== -1) continue // Skip fixed length AIs

        if (remaining.startsWith(ai)) {
          // For variable length AIs, find the next AI or separator
          let endPosition = -1

          // Look for the next AI (any 2 digits that are valid AIs)
          for (let i = ai.length; i < remaining.length - 1; i++) {
            const potentialAI = remaining.substring(i, i + 2)
            if (potentialAI in aiLengths) {
              endPosition = i - ai.length
              break
            }
          }

          // Also check for GS1 separator
          const separatorPosition = remaining
            .substring(ai.length)
            .indexOf('\x1D')
          if (
            separatorPosition !== -1 &&
            (endPosition === -1 || separatorPosition < endPosition)
          ) {
            endPosition = separatorPosition
          }

          // Extract the value
          const value =
            endPosition === -1
              ? remaining.substring(ai.length)
              : remaining.substring(ai.length, ai.length + endPosition)

          // Only add if we have a non-empty value
          if (value) {
            identifiers.push({ ai, value })
          }

          // Update remaining string
          if (endPosition === -1) {
            remaining = ''
          } else if (separatorPosition === endPosition) {
            remaining = remaining.substring(ai.length + endPosition + 1)
          } else {
            remaining = remaining.substring(ai.length + endPosition)
          }

          foundMatch = true
          break
        }
      }
    }

    // If no match was found, try to skip past any GS1 separators
    if (!foundMatch) {
      const separatorIndex = remaining.indexOf('\x1D')
      if (separatorIndex !== -1) {
        // Before skipping the separator, check if there's a variable-length AI before it
        for (const [ai, length] of Object.entries(aiLengths)) {
          if (length !== -1) continue // Skip fixed length AIs

          const aiPosition = remaining.lastIndexOf(ai, separatorIndex)
          if (aiPosition !== -1 && aiPosition + ai.length <= separatorIndex) {
            const value = remaining.substring(
              aiPosition + ai.length,
              separatorIndex
            )
            if (value) {
              identifiers.push({ ai, value })
            }
            remaining = remaining.substring(separatorIndex + 1)
            foundMatch = true
            break
          }
        }

        if (!foundMatch) {
          remaining = remaining.substring(separatorIndex + 1)
          continue
        }
      }
      if (!foundMatch) {
        break
      }
    }
  }

  // Sort identifiers to maintain order (GTIN first, then others)
  identifiers.sort((a, b) => {
    if (a.ai === '01') return -1
    if (b.ai === '01') return 1
    return 0
  })

  return identifiers
}

/**
 * Checks if a barcode contains only a device identifier (AI 01)
 */
export function isDeviceIdOnlyBarcode(barcodeText: string): boolean {
  const identifiers = parseGS1Barcode(barcodeText)
  return identifiers.length === 1 && identifiers[0].ai === '01'
}

/**
 * Checks if a barcode appears to be missing essential identifiers
 * Essential identifiers typically include device ID (01)
 */
export function isMissingEssentialIdentifiers(barcodeText: string): boolean {
  const identifiers = parseGS1Barcode(barcodeText)
  const hasDeviceId = identifiers.some((id) => id.ai === '01')

  // If it doesn't have a device ID, it's likely missing essential information
  return !hasDeviceId && identifiers.length > 0
}

/**
 * Checks if a barcode might be part of a split set by analyzing common patterns
 * This helps detect when we should wait for additional barcodes
 */
export function isPotentiallySplitBarcode(barcodeText: string): boolean {
  const identifiers = parseGS1Barcode(barcodeText)

  // If it has a device ID (01), it's likely complete unless it's device-ID-only
  const hasDeviceId = identifiers.some((id) => id.ai === '01')
  if (hasDeviceId) {
    return isDeviceIdOnlyBarcode(barcodeText)
  }

  // If it doesn't have a device ID but has other identifiers, it might be a split barcode
  // Common split scenarios: lot number (10), expiration date (17), serial number (21)
  const hasCommonSplitIdentifiers = identifiers.some((id) =>
    ['10', '17', '21', '11', '13', '15'].includes(id.ai)
  )

  return hasCommonSplitIdentifiers
}

/**
 * Combines multiple barcode data strings into a single GS1 formatted string
 * Removes duplicates and maintains proper GS1 ordering
 */
export function combineBarcodeData(barcodeDataArray: string[]): string {
  const allIdentifiers: GS1Identifier[] = []

  // Parse each barcode individually
  for (const barcodeData of barcodeDataArray) {
    if (isGS1UDI(barcodeData)) {
      const identifiers = parseGS1Barcode(barcodeData)
      allIdentifiers.push(...identifiers)
    }
  }

  // Remove duplicates based on AI (Application Identifier)
  // Keep the first occurrence of each AI
  const uniqueIdentifiers = allIdentifiers.filter(
    (identifier, index, self) =>
      index === self.findIndex((i) => i.ai === identifier.ai)
  )

  // Sort identifiers to maintain order (GTIN first, then others)
  uniqueIdentifiers.sort((a, b) => {
    if (a.ai === '01') return -1
    if (b.ai === '01') return 1
    return 0
  })

  // Construct the combined barcode string with parentheses
  return uniqueIdentifiers.map(({ ai, value }) => `(${ai})${value}`).join('')
}

/**
 * Formats GS1 identifiers into a properly formatted barcode string
 */
export function formatGS1Barcode(identifiers: GS1Identifier[]): string {
  // Sort identifiers to maintain order (GTIN first, then others)
  const sortedIdentifiers = [...identifiers].sort((a, b) => {
    if (a.ai === '01') return -1
    if (b.ai === '01') return 1
    return 0
  })

  // Construct the barcode string with parentheses
  return sortedIdentifiers.map(({ ai, value }) => `(${ai})${value}`).join('')
}

/**
 * Checks if collected barcodes form a potentially complete set
 * This can help determine if we should finish collection early
 */
export function hasCompleteIdentifierSet(barcodeDataArray: string[]): boolean {
  if (barcodeDataArray.length === 0) return false

  const allIdentifiers: GS1Identifier[] = []

  // Parse all collected barcodes and gather their identifiers
  for (const barcodeData of barcodeDataArray) {
    if (isGS1UDI(barcodeData)) {
      const identifiers = parseGS1Barcode(barcodeData)
      allIdentifiers.push(...identifiers)
    }
  }

  // Check if we have essential identifiers
  const hasDeviceId = allIdentifiers.some((id) => id.ai === '01')
  const hasAdditionalInfo = allIdentifiers.some((id) =>
    ['10', '17', '21', '11', '13', '15'].includes(id.ai)
  )

  // Consider complete if we have device ID and at least one additional identifier
  return hasDeviceId && hasAdditionalInfo
}
